<script setup lang="ts">
import { ref, watch } from 'vue'

import MediaImage from './MediaImage.vue'
import type { MediaVideoProps } from '@fondof-ui/components/media/types'
import { useVideo } from '@fondof-ui/composables/media/useVideo'

const videoRef = ref<HTMLVideoElement | null>(null)

const props = defineProps<MediaVideoProps>()

const {
  videoSrc,
  isVideoPlaying,
  resetVideoState,
  playOrPause,
  hasSelfHostedVideo,
  videoIdUrl,
  videoPreload,
  videoAttributes,
  showPreviewImage,
  showPlayIcon,
} = useVideo(props, videoRef)

const emit = defineEmits<{
  'update:video-playing': [isVideoPlaying: boolean]
}>()

watch(isVideoPlaying, () => {
  emit('update:video-playing', isVideoPlaying.value)
})
</script>

<template>
  <div
    :class="[
      'relative size-full cursor-pointer',
      {
        'pb-[56.25%]': !hasSelfHostedVideo && videoIdUrl,
      },
    ]"
    @click="playOrPause()"
  >
    <video
      v-if="hasSelfHostedVideo"
      ref="videoRef"
      data-chromatic="ignore"
      class="size-full object-cover"
      :preload="
        !showPreviewImage && !videoAutoplay && videoPreload === 'none'
          ? 'metadata'
          : videoPreload
      "
      v-bind="videoAttributes"
      :src="videoSrc"
      @play="isVideoPlaying = true"
      @pause="isVideoPlaying = false"
      @ended="resetVideoState()"
    >
      <!-- TODO: support different/all languages -->
      <track
        v-if="props.captionsSrc"
        kind="captions"
        :src="props.captionsSrc"
        srclang="de"
        label="Deutsch"
        default
      />
    </video>

    <iframe
      v-else-if="videoIdUrl"
      class="absolute left-0 top-0 size-full"
      :src="videoIdUrl"
    />

    <div
      v-if="hasSelfHostedVideo && (showPreviewImage || showPlayIcon)"
      class="absolute top-0 block size-full cursor-pointer"
    >
      <slot
        v-if="showPlayIcon"
        name="playIcon"
      />
      <MediaImage
        v-if="showPreviewImage"
        cover-image
        :image-mobile="imageMobile"
        :image-tablet="imageTablet"
        :image-desktop="imageDesktop"
        :lazyload="lazyload ?? undefined"
      />
    </div>
  </div>
</template>
